import { useEffect, useRef } from "react";
import * as d3 from "d3";
import { apiFetch } from "../../api/apiClient";

const HEIGHT = 520;

const margin = {
  top: 20,
  right: 10,
  bottom: 110,
  left: 80,
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 20;
// Multiplicative step used by the keyboard +/- zoom controls.
const ZOOM_STEP = 1.5;
// One step of the keyboard navigation, in data-point indices (5 min each).
// Shift+Arrow multiplies this up to a whole hour (12 * 5 min = 60 min).
const HOUR_STEP = 12;
const LAST_INTERVAL_INDEX = 287;
const SERIES_COLORS = {
  max: "#af0000",
  min: "#53c000",
  avg: "#2d8dc5",
};
const tooltipSwatchStyle = color => ({
  display: "inline-block",
  width: "15px",
  borderTop: `3px solid ${color}`,
  marginRight: "6px",
  verticalAlign: "middle",
});
// Empirically chosen so a 4-line tooltip rendered with ChartTooltip's
// translate(-50%, -100%) appears visually centered on the chart's vertical
// midline (instead of sitting with its bottom edge on the midline).
const TOOLTIP_CENTER_OFFSET = 40;

// Pick an integer tick step (in 5-minute data-point indices) so the x-axis
// stays readable as the user zooms in. Steps are chosen so labels never crowd:
//   12 -> 1 hour, 6 -> 30 min, 3 -> 15 min, 2 -> 10 min, 1 -> 5 min
function pickXTickStep(visibleSpan) {
  if (visibleSpan > 200) return 12;
  if (visibleSpan > 100) return 6;
  if (visibleSpan > 50) return 3;
  if (visibleSpan > 20) return 2;
  return 1;
}

function formatTime(index) {
  const minutes = index * 5;
  const h = String(Math.floor(minutes / 60)).padStart(2, "0");
  const m = String(minutes % 60).padStart(2, "0");
  return `${h}:${m}`;
}

export function normalizeAuxLoad24HrRows(payload) {
  const rows = Array.isArray(payload)
    ? payload
    : typeof payload === "string"
      ? JSON.parse(payload)
      : [];

  return rows
    .map(row => {
      const rawIndex = row?.intervalIndex ?? row?.interval_index;
      return {
        intervalIndex: rawIndex == null || rawIndex === '' ? NaN : Number(rawIndex),
        max: Number(row?.max),
        min: Number(row?.min),
        avg: Number(row?.avg),
      };
    })
    .filter(
      row =>
        Number.isInteger(row.intervalIndex) &&
        row.intervalIndex >= 0 &&
        row.intervalIndex <= LAST_INTERVAL_INDEX &&
        Number.isFinite(row.max) &&
        Number.isFinite(row.min) &&
        Number.isFinite(row.avg)
    )
    .sort((a, b) => a.intervalIndex - b.intervalIndex);
}

export default function AuxiliaryLoad24HrProfile({
  portfolioName = "gemini",
  duration = "default",
  startDate = "",
  endDate = "",
  data = [],
  onTooltip,
}) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const dataRef = useRef([]);
  // Persist the current zoom transform across resizes / data updates so the
  // user's view is preserved when the chart re-renders.
  const transformRef = useRef(d3.zoomIdentity);
  // Persistent click/keyboard selection — kept across draws so the user's
  // pick survives resizes, focus/blur, and data refreshes.
  const selectedIdxRef = useRef(null);
  // Read the latest tooltip setter at event time so a new callback identity
  // from the parent doesn't tear down the draw effect mid-interaction.
  const onTooltipRef = useRef(onTooltip);
  onTooltipRef.current = onTooltip;

  useEffect(() => {
    if (!containerRef.current) return;

    // Tooltip div fallback (only when page-level ChartTooltip isn't used)
    const tooltip = onTooltipRef.current
      ? null
      : d3
          .select("body")
          .append("div")
          .style("position", "absolute")
          .style("left", "0")
          .style("top", "0")
          .style("background", "white")
          .style("border", "1px solid #ccc")
          .style("padding", "6px")
          .style("border-radius", "4px")
          .style("pointer-events", "none")
          .style("box-shadow", "0 0 10px rgba(0,0,0,0.25)")
          .style("opacity", 0);

    // Bridge ref so the effect-scope keyboard/focus listeners (which outlive
    // a single draw) can always drive the most recent chart instance.
    // Populated at the end of every `draw()` call.
    const handlersRef = { current: null };

    function draw() {
      if (!containerRef.current || !dataRef.current) return;

      const width = containerRef.current.clientWidth;
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = HEIGHT - margin.top - margin.bottom;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      // Clip path so lines & fills stay inside the chart area when zoomed.
      svg
        .append("defs")
        .append("clipPath")
        .attr("id", "aux-load-chart-clip")
        .append("rect")
        .attr("width", innerWidth)
        .attr("height", innerHeight);

      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      /* =========================
         BASE SCALES
         (xBase is rescaled by the zoom transform; yBase never changes because
         this chart only zooms horizontally.)
         ========================= */
      const lastIdx = dataRef.current.length - 1;
      const pathData = dataRef.current.flatMap((row, index) => {
        if (index === 0 || row.intervalIndex - dataRef.current[index - 1].intervalIndex === 1) {
          return [row];
        }
        return [null, row];
      });

      // If the dataset shrank under us (e.g. new duration), clamp any
      // persisted selection so it can't point past the new tail.
      if (selectedIdxRef.current != null) {
        selectedIdxRef.current = Math.max(
          0,
          Math.min(lastIdx, selectedIdxRef.current)
        );
      }

      const xBase = d3
        .scaleLinear()
        // INTERVAL_INDEX is already plant-local Pacific time:
        // 0 = 00:00 and 287 = 23:55. Do not convert it from UTC again.
        .domain([0, LAST_INTERVAL_INDEX])
        .range([0, innerWidth]);

      const nearestDataIdx = (intervalIndex) => {
        if (lastIdx < 0) return null;
        let bestIdx = 0;
        let bestDistance = Infinity;
        dataRef.current.forEach((row, index) => {
          const distance = Math.abs(row.intervalIndex - intervalIndex);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestIdx = index;
          }
        });
        return bestIdx;
      };

      const yBase = d3
        .scaleLinear()
        .domain([0, 14])
        .range([innerHeight, 0]);

      /* =========================
         LAYER GROUPS
         ========================= */
      const gridYG = g.append("g").attr("class", "grid grid-y");
      const axisYG = g.append("g").attr("class", "axis axis-y");
      const axisXG = g
        .append("g")
        .attr("class", "axis axis-x")
        .attr("transform", `translate(0,${innerHeight})`);

      const plotArea = g
        .append("g")
        .attr("class", "plot-area")
        .attr("clip-path", "url(#aux-load-chart-clip)");

      const maxArea = plotArea
        .append("path")
        .attr("fill", "#af0000")
        .attr("opacity", 0.25);
      const maxLine = plotArea
        .append("path")
        .attr("fill", "none")
        .attr("stroke", "#af0000")
        .attr("stroke-width", 3);

      const minArea = plotArea
        .append("path")
        .attr("fill", "#53c000")
        .attr("opacity", 0.25);
      const minLine = plotArea
        .append("path")
        .attr("fill", "none")
        .attr("stroke", "#53c000")
        .attr("stroke-width", 3);

      const avgArea = plotArea
        .append("path")
        .attr("fill", "#2d8dc5")
        .attr("opacity", 0.25);
      const avgLine = plotArea
        .append("path")
        .attr("fill", "none")
        .attr("stroke", "#2d8dc5")
        .attr("stroke-width", 3);

      /* =========================
         FIXED Y GRID & AXIS
         (independent of zoom — Y never rescales)
         ========================= */
      gridYG
        .call(
          d3.axisLeft(yBase)
            .tickValues(d3.range(0, 14.1, 2))
            .tickSize(-innerWidth)
            .tickFormat("")
        )
        .selectAll("line")
        .attr("stroke", "#d9d9d9");
      gridYG.select(".domain").remove();

      axisYG.call(
        d3.axisLeft(yBase)
          .tickValues(d3.range(0, 14.1, 2))
          .tickFormat(d3.format(".3f"))
      );

      /* =========================
         CROSSHAIR + INTERACTION OVERLAY
         ========================= */
      const crosshair = g.append("line")
        .attr("stroke", "#666")
        .attr("y1", 0)
        .attr("y2", innerHeight)
        .style("pointer-events", "none")
        .style("opacity", 0);

      const overlay = g
        .append("rect")
        .attr("class", "interaction-overlay")
        .attr("width", innerWidth)
        .attr("height", innerHeight)
        .attr("fill", "transparent")
        .style("cursor", "crosshair");

      /* =========================
         Y‑AXIS LABEL
         ========================= */
      g.append("text")
        .attr("transform", "rotate(-90)")
        .attr("x", -innerHeight / 2)
        .attr("y", -60)
        .attr("text-anchor", "middle")
        .style("font-size", "14px")
        .style("font-weight", "bold")
        .text("Power, MW");

      /* =========================
         ZOOM-DRIVEN RENDER
         ========================= */
      let xScale = xBase;
      const yScale = yBase;

      function applyTransform(transform) {
        transformRef.current = transform;
        xScale = transform.rescaleX(xBase);

        const [xMin, xMax] = xScale.domain();
        const visibleSpan = Math.max(0, xMax - xMin);
        const step = pickXTickStep(visibleSpan);

        const startTick = Math.max(0, Math.ceil(xMin / step) * step);
        const endTick = Math.min(LAST_INTERVAL_INDEX, xMax);
        const xTickValues =
          endTick >= startTick
            ? d3.range(startTick, endTick + 0.0001, step)
            : [];
        if (
          xMax >= LAST_INTERVAL_INDEX
          && xTickValues[xTickValues.length - 1] !== LAST_INTERVAL_INDEX
        ) {
          xTickValues.push(LAST_INTERVAL_INDEX);
        }

        axisXG
          .call(
            d3.axisBottom(xScale)
              .tickValues(xTickValues)
              .tickFormat((i) => formatTime(i))
          )
          .selectAll("text")
          .attr("transform", "rotate(90)")
          .attr("text-anchor", "start")
          .attr("dx", "1em")
          .attr("dy", "-0.5em");

        const lineGen = (accessor) =>
          d3.line()
            .defined((d) => d != null)
            .x((d) => xScale(d.intervalIndex))
            .y((d) => yScale(accessor(d)));

        const areaGen = (accessor) =>
          d3.area()
            .defined((d) => d != null)
            .x((d) => xScale(d.intervalIndex))
            .y0(yScale(0))
            .y1((d) => yScale(accessor(d)));

        maxArea.datum(pathData).attr("d", areaGen((d) => d.max));
        maxLine.datum(pathData).attr("d", lineGen((d) => d.max));
        minArea.datum(pathData).attr("d", areaGen((d) => d.min));
        minLine.datum(pathData).attr("d", lineGen((d) => d.min));
        avgArea.datum(pathData).attr("d", areaGen((d) => d.avg));
        avgLine.datum(pathData).attr("d", lineGen((d) => d.avg));
      }

      /* =========================
         TOOLTIP / HOVER HANDLERS
         ========================= */
      let isDragging = false;
      // Mouse hover index for this draw cycle. The persistent click/keyboard
      // selection lives on `selectedIdxRef` (component scope).
      let hoveredIdx = null;

      const cursorForZoom = (k) => (k > 1 ? "grab" : "crosshair");

      const hideTooltip = () => {
        if (onTooltipRef.current) {
          onTooltipRef.current((t) => ({ ...t, visible: false }));
        } else {
          tooltip?.style("opacity", 0);
        }
      };

      const hideCrosshair = () => {
        crosshair.style("opacity", 0);
      };

      // Mouse hover wins when present; otherwise the persistent keyboard /
      // click selection drives the indicator while the SVG is focused.
      function activeIdx() {
        if (hoveredIdx != null) return hoveredIdx;
        if (
          selectedIdxRef.current != null &&
          svgRef.current &&
          document.activeElement === svgRef.current
        ) {
          return selectedIdxRef.current;
        }
        return null;
      }

      // Render the crosshair + tooltip for a snapped data-point index.
      // - x snaps to the data point (so hover/keyboard share one geometry).
      // - y is pinned to the chart's vertical center, so the tooltip never
      //   bobs up and down with cursor movement.
      // - Indices outside the (zoomed) visible range hide everything to
      //   avoid drawing affordances over the axis labels.
      function showAtIdx(idx) {
        if (idx == null || idx < 0 || idx > lastIdx) {
          hideCrosshair();
          hideTooltip();
          return;
        }
        const d = dataRef.current[idx];
        if (!d) {
          hideCrosshair();
          hideTooltip();
          return;
        }

        const cx = xScale(d.intervalIndex);
        if (cx < 0 || cx > innerWidth) {
          hideCrosshair();
          hideTooltip();
          return;
        }

        crosshair.attr("x1", cx).attr("x2", cx).style("opacity", 1);

        const time = formatTime(d.intervalIndex);
        const svgEl = svgRef.current;
        if (!svgEl) return;
        const rect = svgEl.getBoundingClientRect();

        const tooltipX = rect.left + margin.left + cx;
        const tooltipY =
          rect.top + margin.top + innerHeight / 2 + TOOLTIP_CENTER_OFFSET;

        if (onTooltipRef.current) {
          onTooltipRef.current({
            visible: true,
            x: tooltipX,
            y: tooltipY,
            content: (
              <>
                <strong>Time:</strong> {time}
                <br />
                <span
                  aria-hidden="true"
                  style={tooltipSwatchStyle(SERIES_COLORS.max)}
                />
                <strong>MAX:</strong> {Number(d.max).toFixed(3)} MW
                <br />
                <span
                  aria-hidden="true"
                  style={tooltipSwatchStyle(SERIES_COLORS.min)}
                />
                <strong>MIN:</strong> {Number(d.min).toFixed(3)} MW
                <br />
                <span
                  aria-hidden="true"
                  style={tooltipSwatchStyle(SERIES_COLORS.avg)}
                />
                <strong>AVG:</strong> {Number(d.avg).toFixed(3)} MW
              </>
            ),
          });
        } else {
          tooltip
            ?.style("opacity", 1)
            .html(
              `<strong>Time:</strong> ${time}<br/>` +
              `<span aria-hidden="true" style="display:inline-block;width:15px;border-top:3px solid ${SERIES_COLORS.max};margin-right:6px;vertical-align:middle"></span>` +
              `<strong>MAX:</strong> ${d.max.toFixed(3)} MW<br/>` +
              `<span aria-hidden="true" style="display:inline-block;width:15px;border-top:3px solid ${SERIES_COLORS.min};margin-right:6px;vertical-align:middle"></span>` +
              `<strong>MIN:</strong> ${d.min.toFixed(3)} MW<br/>` +
              `<span aria-hidden="true" style="display:inline-block;width:15px;border-top:3px solid ${SERIES_COLORS.avg};margin-right:6px;vertical-align:middle"></span>` +
              `<strong>AVG:</strong> ${d.avg.toFixed(3)} MW`
            )
            .style("left", `${tooltipX + window.scrollX}px`)
            .style("top", `${tooltipY + window.scrollY}px`);
        }
      }

      function refreshIndicator() {
        const idx = activeIdx();
        if (idx == null) {
          hideCrosshair();
          hideTooltip();
          return;
        }
        showAtIdx(idx);
      }

      function handleHover(mx) {
        if (lastIdx < 0) return;
        const idx = nearestDataIdx(xScale.invert(mx));
        hoveredIdx = idx;
        showAtIdx(idx);
      }

      overlay
        .on("mousemove", (event) => {
          if (isDragging) return;
          const [mx] = d3.pointer(event);
          handleHover(mx);
        })
        .on("mouseleave", () => {
          if (isDragging) return;
          hoveredIdx = null;
          // Falls back to the persistent selection if the SVG still has
          // keyboard focus, otherwise hides everything.
          refreshIndicator();
        })
        .on("click", (event) => {
          if (isDragging) return;
          if (lastIdx < 0) return;
          const [mx] = d3.pointer(event);
          const idx = nearestDataIdx(xScale.invert(mx));
          selectedIdxRef.current = idx;
          // Focus the SVG so subsequent arrow / +/- keys are captured by
          // the keyboard listeners.
          svgRef.current?.focus({ preventScroll: true });
          refreshIndicator();
        });

      /* =========================
         ZOOM + PAN (horizontal only)
         - wheel up   -> zoom in toward pointer
         - wheel down -> zoom out, clamped at the default 1x view
         - click+drag -> pan horizontally (only when zoomed in; bounded by
                         translateExtent to the original data range)
         - keyboard   -> +/- via `zoomBy`, anchored on the selected point
         ========================= */
      const zoom = d3.zoom()
        .scaleExtent([MIN_ZOOM, MAX_ZOOM])
        .extent([
          [0, 0],
          [innerWidth, innerHeight],
        ])
        .translateExtent([
          [0, 0],
          [innerWidth, innerHeight],
        ])
        .filter((event) => {
          if (event.type === "wheel") return true;
          if (event.type === "mousedown") {
            return transformRef.current.k > MIN_ZOOM;
          }
          return false;
        })
        .on("start", (event) => {
          if (event.sourceEvent && event.sourceEvent.type === "mousedown") {
            isDragging = false;
          }
        })
        .on("zoom", (event) => {
          applyTransform(event.transform);

          const sourceEvent = event.sourceEvent;
          if (!sourceEvent) {
            // Programmatic pan/zoom (keyboard +/- or navigate auto-pan).
            // Re-render the persistent indicator at the new pixel coords.
            refreshIndicator();
            return;
          }

          if (sourceEvent.type === "mousemove") {
            if (!isDragging) {
              isDragging = true;
              overlay.style("cursor", "grabbing");
              hoveredIdx = null;
              hideCrosshair();
              hideTooltip();
            }
          } else if (sourceEvent.type === "wheel") {
            overlay.style("cursor", cursorForZoom(event.transform.k));
            const [mx] = d3.pointer(sourceEvent, overlay.node());
            handleHover(mx);
          }
        })
        .on("end", () => {
          if (isDragging) {
            isDragging = false;
          }
          overlay.style("cursor", cursorForZoom(transformRef.current.k));
          // After a drag-pan or wheel-zoom, the selected point may now sit
          // somewhere new on screen — re-render the persistent indicator.
          refreshIndicator();
        });

      overlay.style("cursor", cursorForZoom(transformRef.current.k));
      overlay.call(zoom);

      // Restore any prior zoom state (preserved across resizes / re-renders).
      // Setting the transform via the behavior fires the "zoom" handler,
      // which performs the initial render.
      overlay.call(zoom.transform, transformRef.current);

      /* =========================
         KEYBOARD-DRIVEN PAN + ZOOM
         ========================= */

      // Center the visible viewport horizontally on the selected point.
      // The d3 zoom translateExtent (which matches the base pixel range)
      // automatically clamps when the selection is within half a viewport
      // of the data start/end — at that point the selection "unlocks" from
      // center and slides toward the edge, which is exactly what the user
      // expects at the boundaries.
      function centerOnSelected() {
        if (selectedIdxRef.current == null) return;
        if (transformRef.current.k <= MIN_ZOOM) return;
        const selected = dataRef.current[selectedIdxRef.current];
        if (!selected) return;
        overlay.call(
          zoom.translateTo,
          xBase(selected.intervalIndex),
          innerHeight / 2
        );
      }

      function navigate(dx) {
        if (lastIdx < 0) return;
        let cur = selectedIdxRef.current;
        if (cur == null) {
          const [xMin, xMax] = xScale.domain();
          cur = nearestDataIdx((xMin + xMax) / 2);
        }
        let next = cur + dx;
        // Boundary clamp. Shift+Arrow falls back to the last data point when
        // less than an hour of data remains in the requested direction.
        if (next < 0) next = 0;
        if (next > lastIdx) next = lastIdx;
        if (next === selectedIdxRef.current) return;
        selectedIdxRef.current = next;
        centerOnSelected();
        // centerOnSelected may have been a no-op (1x zoom or clamped at the
        // boundary) — refresh explicitly so the tooltip always moves.
        refreshIndicator();
      }

      // +/- zoom anchored on the selected point's current pixel position, so
      // the selection stays under the same screen pixel before and after
      // (subject to translateExtent clamping at MIN_ZOOM).
      function zoomBy(steps) {
        const t = transformRef.current;
        const factor = Math.pow(ZOOM_STEP, steps);
        const targetK = Math.min(
          MAX_ZOOM,
          Math.max(MIN_ZOOM, t.k * factor)
        );
        if (targetK === t.k) return;

        let anchorIdx = selectedIdxRef.current;
        if (anchorIdx == null) {
          const [xMin, xMax] = xScale.domain();
          anchorIdx = nearestDataIdx((xMin + xMax) / 2);
        }
        const anchor = dataRef.current[anchorIdx];
        if (!anchor) return;
        const px = xScale(anchor.intervalIndex);
        const py = innerHeight / 2;
        overlay
          .transition()
          .duration(150)
          .call(zoom.scaleBy, targetK / t.k, [px, py]);
      }

      /* =========================
         LEGEND
         ========================= */
      const legend = svg
        .append("g")
        .attr(
          "transform",
          `translate(${width / 2}, ${HEIGHT - 35})`
        )
        .attr("text-anchor", "middle");

      const items = [
        { label: "MAX", color: SERIES_COLORS.max, x: -120 },
        { label: "MIN", color: SERIES_COLORS.min, x: 0 },
        { label: "AVG", color: SERIES_COLORS.avg, x: 120 },
      ];

      items.forEach(item => {
        legend.append("line")
          .attr("x1", item.x - 20)
          .attr("x2", item.x - 5)
          .attr("y1", -2)
          .attr("y2", -2)
          .attr("stroke", item.color)
          .attr("stroke-width", 3);

        legend.append("text")
          .attr("x", item.x + 20)
          .attr("y", 2)
          .style("font-weight", "bold")
          .text(item.label);
      });

      // Publish this draw cycle's nav/zoom/refresh hooks so the effect-
      // scope keyboard listeners always drive the latest chart instance.
      handlersRef.current = {
        navigate,
        zoomBy,
        refresh: refreshIndicator,
        ensureDefaultSelection: () => {
          if (selectedIdxRef.current == null && lastIdx >= 0) {
            const [xMin, xMax] = xScale.domain();
            selectedIdxRef.current = nearestDataIdx((xMin + xMax) / 2);
          }
        },
      };

      // If the chart was already focused when this draw ran (e.g. a resize
      // or data refresh while the user was navigating), re-render the
      // selection so the indicator persists across redraws.
      if (svgRef.current && document.activeElement === svgRef.current) {
        handlersRef.current.ensureDefaultSelection();
        refreshIndicator();
      }
    }

    let cancelled = false;

    const load = async () => {
      if (Array.isArray(data) && data.length > 0) {
        dataRef.current = normalizeAuxLoad24HrRows(data);
        draw();
        return;
      }

      if (!portfolioName) {
        dataRef.current = [];
        draw();
        return;
      }
      try {
        const query = new URLSearchParams();
        if (startDate && endDate) {
          query.set("start", startDate);
          query.set("end", endDate);
        }
        const suffix = query.toString() ? `?${query}` : "";
        const payload = await apiFetch(`/api/graph/portfolio/${portfolioName}/insights/${encodeURIComponent(duration)}/aux-load-24hr${suffix}`);
        if (cancelled) return;
        dataRef.current = normalizeAuxLoad24HrRows(payload.results);
        draw();
      } catch (err) {
        console.warn("Auxiliary load 24hr API fetch failed:", err);
      }
    };

    load();

    const observer = new ResizeObserver(draw);
    observer.observe(containerRef.current);

    /* =========================
       KEYBOARD NAVIGATION
       - Tab to focus, click on the plot to seed the selection.
       - Arrow keys move the selection by one 5-minute interval.
       - Shift+Arrow jumps a whole hour; clamps to first/last data point
         when less than an hour remains in that direction.
       - + / = / NumpadAdd zoom in toward the selected point.
       - - / _ / NumpadSubtract zoom out, keeping the selection anchored.
       Listeners live at effect scope and read from `handlersRef` so they
       always drive whichever draw cycle is currently active.
       ========================= */
    const svgEl = svgRef.current;

    const handleKeyDown = (e) => {
      const h = handlersRef.current;
      if (!h) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        h.navigate(e.shiftKey ? -HOUR_STEP : -1);
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        h.navigate(e.shiftKey ? HOUR_STEP : 1);
        return;
      }
      if (e.key === "+" || e.key === "=" || e.code === "NumpadAdd") {
        e.preventDefault();
        h.zoomBy(1);
        return;
      }
      if (e.key === "-" || e.key === "_" || e.code === "NumpadSubtract") {
        e.preventDefault();
        h.zoomBy(-1);
      }
    };

    const handleFocus = () => {
      const h = handlersRef.current;
      if (!h) return;
      h.ensureDefaultSelection();
      h.refresh();
    };

    const handleBlur = () => {
      const h = handlersRef.current;
      if (!h) return;
      // refresh() sees `document.activeElement !== svgEl` and hides the
      // selection-driven indicator + tooltip.
      h.refresh();
    };

    svgEl?.addEventListener("keydown", handleKeyDown);
    svgEl?.addEventListener("focus", handleFocus);
    svgEl?.addEventListener("blur", handleBlur);

    return () => {
      cancelled = true;
      observer.disconnect();
      tooltip?.remove?.();
      svgEl?.removeEventListener("keydown", handleKeyDown);
      svgEl?.removeEventListener("focus", handleFocus);
      svgEl?.removeEventListener("blur", handleBlur);
    };
  }, [data, portfolioName, duration, startDate, endDate]);

  return (
    <div ref={containerRef} style={{ width: "100%", minWidth: 0 }}>
      <svg
        ref={svgRef}
        tabIndex={0}
        role="img"
        aria-label="24-hour auxiliary load profile. Click or tab to focus, then use arrow keys to move the selection by 5 minutes (Shift+Arrow for one hour) and + / - to zoom around it."
        style={{
          width: "100%",
          height: HEIGHT,
          display: "block",
        }}
      />
    </div>
  );
}
